import type { Category, UpdateCategoryRequest } from '@avrahami/contracts';
import {
  Alert,
  Badge,
  buttonClass,
  Card,
  CloseIcon,
  IMAGE_UPLOAD_ACCEPT,
  isStringList,
  Loading,
  PageHeader,
  PlusIcon,
  toggleValue,
  TreeList,
  TreeRow,
  useViewState,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useCan } from '../api/session';
import { categoriesQuery, keys, tenantApi } from '../api/tenant';
import { useImageUpload } from '../components/fields';
import { he } from '../i18n/he';
import { categoryForest, type CategoryBranch } from '../lib/categories';
import { errorMessage } from '../lib/forms';
import { mediaUrl } from '../lib/media';

function hasFiles(event: DragEvent): boolean {
  return event.dataTransfer.types.includes('Files');
}

/**
 * A category's picture and its state in the store, changed right in the tree: an image is
 * dragged onto the square (or chosen by clicking it), the state is picked from a list. Without
 * a picture of the store's own the square shows the catalog's, which has nothing to remove. Each
 * change is saved at once against the version the row shows; when it fails (someone changed the
 * category meanwhile) the categories are read again and the row says why.
 */
function CategoryQuickEdit({ category }: { category: Category }) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const save = useMutation({
    mutationFn: (change: Omit<UpdateCategoryRequest, 'version'>) =>
      tenantApi.updateCategory(category.id, { ...change, version: category.version }),
    onSuccess: (saved) => {
      queryClient.setQueryData<{ items: Category[] }>(keys.categories(), (current) =>
        current === undefined
          ? current
          : { items: current.items.map((item) => (item.id === saved.id ? saved : item)) },
      );
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: keys.categories() });
    },
  });
  const upload = useImageUpload((media) => {
    save.mutate({ mediaId: media.id });
  });
  const busy = save.isPending || upload.busy;
  const take = (files: FileList | null) => {
    if (busy || files === null || files.length === 0) return;
    save.reset();
    void upload.start([...files].slice(0, 1));
  };
  const { name, image } = category;
  const shown = image?.file ?? category.catalogFile;
  const error =
    upload.error ??
    (save.isError
      ? errorMessage(save.error, { CONFLICT: he.categories.changedMeanwhile })
      : undefined);

  let frame = 'ring-1 ring-slate-200 hover:ring-2 hover:ring-brand-500';
  if (dragging) frame = 'bg-brand-50 ring-2 ring-brand-500';
  else if (shown === null) {
    frame =
      'border border-dashed border-slate-300 bg-white text-slate-400 hover:border-brand-500 hover:text-brand-600';
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="category-quick-edit">
      <div className="relative">
        <button
          type="button"
          disabled={busy}
          aria-label={(shown === null
            ? he.categories.addImage
            : he.categories.replaceImage
          ).replace('{name}', name)}
          aria-busy={busy}
          data-testid="category-image-drop"
          data-dragging={String(dragging)}
          onClick={() => input.current?.click()}
          onDragEnter={(event) => {
            if (!hasFiles(event)) return;
            event.preventDefault();
            setDragging(true);
          }}
          onDragOver={(event) => {
            if (!hasFiles(event)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
          }}
          onDragLeave={() => {
            setDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            take(event.dataTransfer.files);
          }}
          className={`flex size-11 items-center justify-center overflow-hidden rounded-md transition ${frame} ${
            busy ? 'cursor-wait opacity-60' : ''
          }`}
        >
          {dragging ? (
            <span className="text-[10px] leading-tight font-medium text-brand-700">
              {he.categories.dropHere}
            </span>
          ) : shown === null ? (
            <PlusIcon />
          ) : (
            <img
              src={mediaUrl(shown)}
              alt=""
              loading="lazy"
              className="size-full bg-slate-100 object-cover"
            />
          )}
        </button>
        {image === null || busy ? null : (
          <button
            type="button"
            aria-label={he.categories.removeImage.replace('{name}', name)}
            data-testid="category-image-remove"
            onClick={() => {
              upload.clear();
              save.mutate({ mediaId: null });
            }}
            className="absolute -end-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-white text-slate-500 shadow ring-1 ring-slate-300 hover:text-red-600"
          >
            <CloseIcon className="size-3" />
          </button>
        )}
        <input
          ref={input}
          type="file"
          accept={IMAGE_UPLOAD_ACCEPT}
          hidden
          data-testid="category-image-input"
          onChange={(event) => {
            take(event.target.files);
            event.target.value = '';
          }}
        />
      </div>
      <select
        aria-label={`${he.categories.status}: ${name}`}
        data-testid="category-visibility"
        value={category.isVisible ? 'visible' : 'hidden'}
        disabled={busy}
        onChange={(event) => {
          upload.clear();
          save.mutate({ isVisible: event.target.value === 'visible' });
        }}
        className={`rounded-full border-0 py-1 ps-3 pe-8 text-xs font-medium ring-1 focus:ring-2 focus:ring-brand-500 ${
          category.isVisible
            ? 'bg-green-50 text-green-800 ring-green-200'
            : 'bg-slate-100 text-slate-700 ring-slate-200'
        } ${busy ? 'cursor-wait opacity-60' : 'cursor-pointer'}`}
      >
        <option value="visible">{he.categories.visible}</option>
        <option value="hidden">{he.categories.hidden}</option>
      </select>
      {error === undefined ? null : (
        <p role="alert" className="w-full text-xs text-red-600" data-testid="category-quick-error">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The categories as the product list shows them: the main categories, each opening to its
 * sub-categories. The open ones are remembered with the screen.
 */
function CategoryTreeList({
  categories,
  canWrite,
  actions,
}: {
  categories: readonly Category[];
  canWrite: boolean;
  actions: (category: Category) => ReactNode;
}) {
  const [open, setOpen] = useViewState<readonly string[]>('open:categories', [], isStringList);
  const renderBranch = ({ category, children }: CategoryBranch, depth: number): ReactNode => (
    <TreeRow
      key={category.id}
      testId="category-row"
      name={category.name}
      depth={depth}
      open={open.includes(category.id)}
      onToggle={
        children.length === 0
          ? undefined
          : () => {
              setOpen((current) => toggleValue(current, category.id));
            }
      }
      aside={
        <div className="flex flex-wrap items-center gap-2 py-1.5 font-normal">
          {children.length === 0 ? null : (
            <span className="text-xs text-slate-500" data-testid="category-sub-count">
              {`${String(children.length)} ${he.categories.subCount}`}
            </span>
          )}
          <span className="text-xs text-slate-500">{he.productOrigin[category.origin]}</span>
          {canWrite ? (
            <CategoryQuickEdit category={category} />
          ) : (
            <Badge tone={category.isVisible ? 'green' : 'slate'}>
              {category.isVisible ? he.categories.visible : he.categories.hidden}
            </Badge>
          )}
          {actions(category)}
        </div>
      }
    >
      {children.length === 0 ? undefined : (
        <ul className="flex flex-col">{children.map((child) => renderBranch(child, depth + 1))}</ul>
      )}
    </TreeRow>
  );
  return (
    <TreeList label={he.categories.title} testId="category-tree">
      {categoryForest(categories).map((branch) => renderBranch(branch, 0))}
    </TreeList>
  );
}

/**
 * The store's category tree. The picture and the state in the store are changed in the tree
 * itself; a category is created and otherwise edited on a page of its own.
 */
export function CategoriesPage() {
  const canWrite = useCan('store.products.write');
  const categories = useQuery(categoriesQuery());
  const items = categories.data?.items ?? [];

  return (
    <>
      <PageHeader
        title={he.categories.title}
        actions={
          canWrite ? (
            <Link to="/categories/new" className={buttonClass()} data-testid="category-create">
              {he.categories.create}
            </Link>
          ) : null
        }
      />
      <Card>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">{he.categories.intro}</p>
          {canWrite && items.length > 0 ? (
            <p className="text-sm text-slate-600">{he.categories.tableHelp}</p>
          ) : null}
          {categories.isPending ? <Loading /> : null}
          {categories.isError ? <Alert>{errorMessage(categories.error)}</Alert> : null}
          {categories.isSuccess && items.length === 0 ? (
            <p className="text-sm text-slate-500">{he.categories.empty}</p>
          ) : null}
          {items.length > 0 ? (
            <CategoryTreeList
              categories={items}
              canWrite={canWrite}
              actions={(category) =>
                canWrite ? (
                  <Link
                    to="/categories/$categoryId"
                    params={{ categoryId: category.id }}
                    className={buttonClass('secondary')}
                    data-testid="category-edit"
                  >
                    {he.common.edit}
                  </Link>
                ) : null
              }
            />
          ) : null}
        </div>
      </Card>
    </>
  );
}
